import type { ReportedWork } from "@/types/workadvisory";

/** El score que vale: el de la persona si lo puso, si no el sugerido. */
export const effectiveScore = (work: ReportedWork): number | null =>
  work.score ?? work.suggestedScore;

/**
 * La banda de color del score. Los cortes son los mismos de la escala del
 * clasificador, para que un 70 signifique lo mismo en la pantalla y en el prompt.
 */
export function scoreTone(score: number | null): string {
  if (score === null) return "bg-muted text-muted-foreground";
  if (score >= 75)
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
  if (score >= 50)
    return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
  return "bg-muted text-muted-foreground";
}

/** Cómo se le muestra el veredicto al cliente. */
export const VERDICT_LABELS: Record<
  NonNullable<ReportedWork["verdict"]>,
  { label: string; tone: string }
> = {
  CALIFICA: {
    label: "Califica",
    tone: "bg-emerald-600 text-white",
  },
  REVISAR: {
    label: "Revisar",
    tone: "bg-amber-500 text-white",
  },
  NO_CALIFICA: {
    label: "No califica",
    tone: "bg-slate-500 text-white",
  },
};

/**
 * De mejor a peor oportunidad, por tramos. Espeja `byOpportunity` del backend
 * —el informe se guarda ya ordenado, pero un score ajustado a mano en la revisión
 * tiene que reubicarse sin regenerar—.
 *
 *   1. CALIFICA y alcanza la valla · 2. CALIFICA sin llegar
 *   3. REVISAR y alcanza · 4. REVISAR sin llegar · 5. NO CALIFICA
 */
function opportunityTier(work: ReportedWork): number {
  const verdict = work.verdict ?? "REVISAR";
  if (verdict === "NO_CALIFICA") return 5;
  const missesBar = work.analysis?.alcanzaLaValla === false;
  if (verdict === "CALIFICA") return missesBar ? 2 : 1;
  return missesBar ? 4 : 3;
}

/**
 * Qué proporción de los puntos en juego acredita, contando lo que está a su
 * alcance. `null` cuando no hay cruce o el pliego no reparte puntos: sin dato no
 * se penaliza, se pasa al score.
 */
function pointsRatio(work: ReportedWork): number | null {
  const points = work.analysis;
  if (!points || !points.disponible) return null;
  return (points.acreditado + points.alcanzable) / points.disponible;
}

/**
 * Dentro del tramo manda el puntaje del pliego y no el score. El score sale del
 * clasificador —tipo de obra × acceso— y no sabe nada del cruce, así que una obra
 * que encaja perfecto pero acredita 25 de 100 se ponía encima de una que acredita
 * 80. El score sigue desempatando cuando no hay puntaje que comparar.
 */
export const byScoreDesc = (a: ReportedWork, b: ReportedWork) => {
  const tier = opportunityTier(a) - opportunityTier(b);
  if (tier !== 0) return tier;

  const [ratioA, ratioB] = [pointsRatio(a), pointsRatio(b)];
  if (ratioA !== null && ratioB !== null && ratioA !== ratioB) {
    return ratioB - ratioA;
  }

  return (effectiveScore(b) ?? -1) - (effectiveScore(a) ?? -1);
};

/** Una obra llega al informe publicado salvo que las bases la descarten. */
export const isPublishable = (work: ReportedWork): boolean =>
  work.forcePublish || !work.hiddenReason;

export const HIDDEN_REASON_LABELS: Record<string, string> = {
  BASES_ILEGIBLES: "No se pudieron leer sus bases",
  MONTO_FUERA_DE_RANGO: "Las bases la valorizan fuera de su rango",
};
